import type { ResizeDirection } from "@/components/rana-resizable";

import { A4_RATIO_LANDSCAPE, A4_RATIO_PORTRAIT } from "../consts";
import type { CaptureArea, ExportFormat, PDFOrientation } from "../types/index";

export function getPdfRatio(orientation: PDFOrientation): number {
  return orientation === "landscape" ? A4_RATIO_LANDSCAPE : A4_RATIO_PORTRAIT;
}

interface ResolveCaptureAreaUpdateOptions {
  containerWidth: number;
  containerHeight: number;
  minCaptureAreaSize: number;
  format: ExportFormat;
  orientation: PDFOrientation;
  orientationOverride?: PDFOrientation;
}

export function resolveCaptureAreaUpdate(
  previous: CaptureArea,
  patch: Partial<CaptureArea>,
  options: ResolveCaptureAreaUpdateOptions
): CaptureArea {
  const {
    containerWidth,
    containerHeight,
    minCaptureAreaSize,
    format,
    orientation,
    orientationOverride,
  } = options;

  let { x, y, width, height } = { ...previous, ...patch };

  if (x < 0) {
    width += x;
  }

  if (y < 0) {
    height += y;
  }

  x = Math.max(0, x);
  y = Math.max(0, y);

  const noXChange = patch.x === undefined;
  const noYChange = patch.y === undefined;

  const noSizeChanges = patch.width === undefined && patch.height === undefined;
  if (noSizeChanges) {
    x = Math.min(x, containerWidth - previous.width);
    y = Math.min(y, containerHeight - previous.height);
    return { ...previous, ...patch, x, y };
  }

  const availableWidth = noXChange ? containerWidth : Math.max(0, containerWidth - x);
  const availableHeight = noYChange ? containerHeight : Math.max(0, containerHeight - y);

  if (format === "pdf") {
    const effectiveOrientation = orientationOverride ?? orientation;
    const ratio = getPdfRatio(effectiveOrientation);

    const widthChanged = patch.width !== undefined;
    const heightChanged = patch.height !== undefined;

    if (widthChanged && !heightChanged) {
      height = width / ratio;
    } else if (!widthChanged && heightChanged) {
      width = height * ratio;
    } else if (widthChanged && heightChanged) {
      const providedRatio = height !== 0 ? width / height : ratio;
      if (Math.abs(providedRatio - ratio) >= 0.01) {
        const widthDiff = Math.abs(width - previous.width);
        const heightDiffAsWidth = Math.abs(height - previous.height) * ratio;

        if (heightDiffAsWidth > widthDiff) {
          width = height * ratio;
        } else {
          height = width / ratio;
        }
      }
    }

    const targetWidth = width;
    const minWidthByRatio = minCaptureAreaSize;
    const maxWidthByBounds = Math.min(availableWidth, availableHeight * ratio);
    if (maxWidthByBounds <= 0) {
      width = 0;
      height = 0;
    } else {
      if (maxWidthByBounds < minWidthByRatio) {
        width = maxWidthByBounds;
      } else {
        width = Math.min(Math.max(targetWidth, minWidthByRatio), maxWidthByBounds);
      }
      height = width / ratio;
    }
  } else {
    width = Math.min(Math.max(width, minCaptureAreaSize), availableWidth);
    height = Math.min(Math.max(height, minCaptureAreaSize), availableHeight);
  }

  if (noXChange) {
    const widthDiff = width - previous.width;
    x = Math.max(0, Math.min(previous.x - widthDiff / 2, containerWidth - width));
  }
  if (noYChange) {
    const heightDiff = height - previous.height;
    y = Math.max(0, Math.min(previous.y - heightDiff / 2, containerHeight - height));
  }

  return { ...previous, ...patch, x, y, width, height };
}

export function getResizePatchFromDelta(
  initial: CaptureArea,
  direction: ResizeDirection,
  delta: { width: number; height: number },
  format: ExportFormat
): Partial<CaptureArea> {
  const normalizedDirection = direction.toLowerCase();
  const touchesLeft = normalizedDirection.includes("left");
  const touchesTop = normalizedDirection.includes("top");
  const touchesHorizontal = touchesLeft || normalizedDirection.includes("right");
  const touchesVertical = touchesTop || normalizedDirection.includes("bottom");

  const width = initial.width + delta.width;
  const height = initial.height + delta.height;
  let x = initial.x;
  let y = initial.y;

  if (touchesLeft) {
    x = initial.x + (initial.width - width);
  }
  if (touchesTop) {
    y = initial.y + (initial.height - height);
  }

  if (format === "pdf") {
    if (touchesVertical && !touchesHorizontal) {
      return { x, y, height };
    }

    return { x, y, width };
  }

  return { x, y, width, height };
}

export function getOrientationCaptureAreaPatch(
  captureArea: CaptureArea,
  orientation: PDFOrientation,
  containerWidth: number,
  containerHeight: number
): Partial<CaptureArea> {
  let width =
    orientation === "landscape"
      ? Math.max(captureArea.width, captureArea.height)
      : Math.min(captureArea.width, captureArea.height);
  const ratio = getPdfRatio(orientation);
  let height = Math.round(width / ratio);

  const tentativeDeltaHeight = height - captureArea.height;
  const tentativeY = Math.max(
    0,
    Math.min(captureArea.y - tentativeDeltaHeight / 2, containerHeight - height)
  );

  const maxHeight = Math.max(0, containerHeight - tentativeY);

  if (containerHeight > 0 && height > maxHeight) {
    width = Math.round(maxHeight * ratio);
    height = maxHeight;
  }

  const deltaWidth = width - captureArea.width;
  const deltaHeight = height - captureArea.height;

  const x = Math.max(0, Math.min(captureArea.x - deltaWidth / 2, containerWidth - width));
  const y = Math.max(0, Math.min(captureArea.y - deltaHeight / 2, containerHeight - height));

  return { x, y, width, height };
}
